// Insights sub-tab: time period, engagement stats, question details, top themes tables.

import React from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(duration);
dayjs.extend(relativeTime);

import { Typography } from "@material-tailwind/react";
import {
    GobbyTable,
    GobbyTableBody,
    GobbyTableCell,
    GobbyTableHead,
    GobbyTableHeader,
    GobbyTableRow,
} from "components/ui/gobby-table";
import { formatBoolean } from "../data-transforms";
import { StatTile } from "./stat-tile";

export function InsightsTab({
    surveyTimePeriod,
    completionData,
    totalResponses,
    totalVotes,
    totalModeratedResponses,
    totalUnThemed,
    questionDetails,
    topThemesForSurvey,
    allResponsesByThemePerQ,
    topThemesForEachQuestion,
}) {
    return (
        <>
            {/* Survey Time Period Section */}
            <div className="mb-6">
                <Typography variant="h5" className="mb-1">
                    Survey Time Period
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <StatTile label="First Survey Started">
                        {surveyTimePeriod.started || "N/A"}
                    </StatTile>
                    <StatTile label="Last Survey Received">
                        {surveyTimePeriod.finished || "N/A"}
                    </StatTile>
                    <StatTile label="Average Completion Time">
                        {surveyTimePeriod.avgCompletionTime ?
                            dayjs.duration(surveyTimePeriod.avgCompletionTime).humanize() :
                            "N/A"}
                    </StatTile>
                </div>
            </div>

            {/* Engagement Statistics Section */}
            <div className="mb-6">
                <Typography variant="h5" className="mb-1">
                    Engagement Statistics
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatTile label="Total Contributors">
                        {completionData?.total_respondents || 0}
                    </StatTile>
                    <StatTile label="Completed Surveys">
                        {completionData?.respondents_finished || 0}
                    </StatTile>
                    <StatTile label="Incomplete Surveys">
                        {(completionData?.total_respondents - completionData?.respondents_finished) || 0}
                    </StatTile>
                    <StatTile label="Completion Rate">
                        {completionData?.total_respondents > 0 ?
                            ((completionData.respondents_finished / completionData.total_respondents) * 100).toFixed(2) + "%" :
                            "0.00%"}
                    </StatTile>
                    <StatTile label="Total Responses">
                        {totalResponses ?? "N/A"}
                    </StatTile>
                    <StatTile label="Total Matched Responses">
                        {totalVotes ?? "N/A"}
                    </StatTile>
                    <StatTile label="Total Moderated Responses">
                        {totalModeratedResponses ?? "N/A"}
                    </StatTile>
                    <StatTile label="Total Un-themed Responses">
                        {totalUnThemed ?? "N/A"}
                    </StatTile>
                </div>
            </div>

            {/* Question Details Table */}
            <div className="mb-6">
                <Typography variant="h5" className="mb-1">
                    Question Details
                </Typography>
                <GobbyTable>
                    <GobbyTableHead>
                        <GobbyTableRow>
                            <GobbyTableHeader>Question Number</GobbyTableHeader>
                            <GobbyTableHeader>Question Text</GobbyTableHeader>
                            <GobbyTableHeader>Question Type</GobbyTableHeader>
                            <GobbyTableHeader>Preset Options</GobbyTableHeader>
                            <GobbyTableHeader>Response Type</GobbyTableHeader>
                            <GobbyTableHeader>Demographic</GobbyTableHeader>
                            <GobbyTableHeader>PII</GobbyTableHeader>
                        </GobbyTableRow>
                    </GobbyTableHead>
                    <GobbyTableBody>
                        {questionDetails.sort((a, b) => a.number - b.number).map((question, index) => (
                            <GobbyTableRow key={index}>
                                <GobbyTableCell>{question.number}</GobbyTableCell>
                                <GobbyTableCell>{question.text}</GobbyTableCell>
                                <GobbyTableCell>{question.type}</GobbyTableCell>
                                <GobbyTableCell>{question.numPresets}</GobbyTableCell>
                                <GobbyTableCell>{question.responseType}</GobbyTableCell>
                                <GobbyTableCell>{formatBoolean(question.demographic)}</GobbyTableCell>
                                <GobbyTableCell>{formatBoolean(question.pii)}</GobbyTableCell>
                            </GobbyTableRow>
                        ))}
                    </GobbyTableBody>
                </GobbyTable>
            </div>

            {/* Top Three Themes for the Survey Section */}
            <div className="mb-6">
                <Typography variant="h5" className="mb-1">
                    Top Three Themes for the Survey
                </Typography>
                <GobbyTable>
                    <GobbyTableHead>
                        <GobbyTableRow>
                            <GobbyTableHeader>Rank</GobbyTableHeader>
                            <GobbyTableHeader>Theme Name</GobbyTableHeader>
                            <GobbyTableHeader>Which Questions Contributed</GobbyTableHeader>
                        </GobbyTableRow>
                    </GobbyTableHead>
                    <GobbyTableBody>
                        {topThemesForSurvey && topThemesForSurvey.length > 0 ? (
                            topThemesForSurvey.map((theme, index) => {
                                const contributingQuestions = allResponsesByThemePerQ
                                    .filter(q => q.themes.some(t => t.theme_id === theme.id))
                                    .map(q => q.question_position)
                                    .join(", ");
                                return (
                                    <GobbyTableRow key={theme.id}>
                                        <GobbyTableCell>{theme.rank || index + 1}</GobbyTableCell>
                                        <GobbyTableCell>{theme.name}</GobbyTableCell>
                                        <GobbyTableCell>{contributingQuestions ?? "N/A"}</GobbyTableCell>
                                    </GobbyTableRow>
                                );
                            })
                        ) : (
                            <GobbyTableRow>
                                <GobbyTableCell colSpan={3} className="text-center">
                                    No themes found for this survey
                                </GobbyTableCell>
                            </GobbyTableRow>
                        )}
                    </GobbyTableBody>
                </GobbyTable>
            </div>

            {/* Top Three Themes for Each Question Section */}
            <div className="mb-6">
                <Typography variant="h5" className="mb-1">
                    Top Three Themes for Each Question
                </Typography>
                <GobbyTable>
                    <GobbyTableHead>
                        <GobbyTableRow>
                            <GobbyTableHeader>Question Number</GobbyTableHeader>
                            <GobbyTableHeader>Text</GobbyTableHeader>
                            <GobbyTableHeader>Reason if not themed</GobbyTableHeader>
                            <GobbyTableHeader>Theme Rank</GobbyTableHeader>
                            <GobbyTableHeader>Theme Name</GobbyTableHeader>
                        </GobbyTableRow>
                    </GobbyTableHead>
                    <GobbyTableBody>
                        {topThemesForEachQuestion?.sort((a, b) => a.questionNumber - b.questionNumber).map((question) => (
                            <React.Fragment key={question.questionNumber}>
                                {question.topThemes.map((theme, index) => (
                                    <GobbyTableRow key={`${question.questionNumber}-${index}`}>
                                        {index === 0 && (
                                            <>
                                                <GobbyTableCell rowSpan={question.topThemes.length || 1}>{question.questionNumber}</GobbyTableCell>
                                                <GobbyTableCell rowSpan={question.topThemes.length || 1}>{question.questionText}</GobbyTableCell>
                                                <GobbyTableCell rowSpan={question.topThemes.length || 1}>{question.reasonIfNotThemed}</GobbyTableCell>
                                            </>
                                        )}
                                        <GobbyTableCell>{theme.rank}</GobbyTableCell>
                                        <GobbyTableCell>{theme.name}</GobbyTableCell>
                                    </GobbyTableRow>
                                ))}
                                {question.topThemes.length === 0 && (
                                    <GobbyTableRow key={`${question.questionNumber}-empty`}>
                                        <GobbyTableCell>{question.questionNumber}</GobbyTableCell>
                                        <GobbyTableCell>{question.questionText}</GobbyTableCell>
                                        <GobbyTableCell>{question.reasonIfNotThemed}</GobbyTableCell>
                                        <GobbyTableCell>-</GobbyTableCell>
                                        <GobbyTableCell>-</GobbyTableCell>
                                    </GobbyTableRow>
                                )}
                            </React.Fragment>
                        ))}
                    </GobbyTableBody>
                </GobbyTable>
            </div>
        </>
    );
}
